'use client';

import Image, { ImageProps } from 'next/image';
import { cloudfrontLoader, createCloudfrontLoader } from '../../helpers/imageLoaders/cloudfront';
import { contentfulLoader, createContentfulLoader } from '../../helpers/imageLoaders/contentful';
import { createImgProxyLoader, imgProxyLoader } from '../../helpers/imageLoaders/imgProxy';

export type customLoaderProps = imgProxyLoader | contentfulLoader | cloudfrontLoader;
export type NextImageWithLoaderProps = ImageProps & customLoaderProps;

/**
 * A wrapper component for Next.js `Image` that supports custom image loaders (Cloudfront, Contentful, ImgProxy)
 * with additional loader-specific props.
 *
 * @param customLoaderType - The type of custom loader to use ('cloudfront', 'contentful', or 'imgProxy').
 * @param origWidth - The original width of the image (used by the loader, to calculate aspect ratio).
 * @param origHeight - The original height of the image (used by the loader, to calculate aspect ratio).
 * @param customLoaderFilters - Optional filters to apply via the loader.
 * @param rest - All other props supported by Next.js `Image`.
 *
 * @throws Error if an unknown loader type is provided.
 *
 * @example
 * ```tsx
 * <NextImageWithLoader
 *   src="/path/to/image.jpg"
 *   alt="Example"
 *   width={400}
 *   height={300}
 *   customLoaderType="cloudfront"
 *   origWidth={800}
 *   origHeight={600}
 *   customLoaderFilters={{ quality: 80 }}
 * />
 * ```
 */
export const NextImageWithLoader = ({
  customLoaderType,
  origWidth,
  origHeight,
  customLoaderFilters,
  ...rest
}: NextImageWithLoaderProps) => {
  const loader = (() => {
    switch (customLoaderType) {
      case 'cloudfront':
        return createCloudfrontLoader(origWidth, origHeight, customLoaderFilters);
      case 'contentful':
        return createContentfulLoader(origWidth, origHeight, customLoaderFilters);
      case 'imgProxy':
        return createImgProxyLoader(origWidth, origHeight, customLoaderFilters);
      default:
        throw new Error(`Unknown loader type`);
    }
  })();

  return <Image loader={loader} {...rest} />;
};
